import { Box, SxProps, Theme } from "@mui/material";
import React from "react";

// The blue of the logo's inner triangle
export const LOGO_BLUE = "#38b6ff";

// How a turn or a growth starts and stops: slow, quick, slow
const EASE = "cubic-bezier(0.65, 0, 0.35, 1)";

// The same, softer, for the variant that never rests
const SOFT_EASE = "cubic-bezier(0.45, 0, 0.55, 1)";

// The mark in the logo's own units, all three triangles equilateral and
// around one centre, so that a third of a turn or a half flip lays each on
// itself: the outer triangle, the hole cut into it, and the inner triangle
export const OUTER = "M244 0 L488 422.62 L0 422.62 Z";
export const HOLE = "M244 124.71 L380 360.27 L108 360.27 Z";
export const INNER = "M244 200.92 L314 322.16 L174 322.16 Z";
const CENTRE = "244px 281.75px";

// How much the inner triangle grows to cover the whole mark
const INNER_TO_OUTER = 3.486;

// The square around the circle the corners sweep as the triangles turn, so
// that no corner is cut at any point of a turn. The mark rests with its
// centre in the middle of it.
const VIEW_BOX = "-37.75 0 563.5 563.5";

// The parts that move. The plain mark has the ring, the inner triangle and,
// for drawing, the ring's outline. The layered mark, for the continuous
// emerge, stacks from the bottom the ring as it was, the blue that grows
// over it, the ring that emerges from that blue, and the blue that emerges
// in the new ring's hole.
type Part =
  | "ring"
  | "inner"
  | "trace"
  | "oldRing"
  | "cover"
  | "newRing"
  | "newInner";

type Keyframes = Record<string, Record<string, string | number>>;

interface Motion {
  // The time (ms) of one cycle
  cycleMs: number;
  // How each part moves between its keyframes, unless the part says
  easing: string;
  parts: Partial<Record<Part, Keyframes>>;
  partEasing?: Partial<Record<Part, string>>;
}

export type LogoLoaderVariant =
  | "counterTurn"
  | "tick"
  | "coinFlip"
  | "emerge"
  | "draw"
  | "suite"
  | "continuousEmerge"
  | "continuousEmergeNoRest";

// Every variant loops without a visible jump: each ends in a pose that looks
// as it began
const MOTIONS: Record<LogoLoaderVariant, Motion> = {
  // The ring turns a third anticlockwise, the inner one follows clockwise,
  // both rest
  counterTurn: {
    cycleMs: 2400,
    easing: EASE,
    parts: {
      ring: {
        "0%, 8%": { transform: "rotate(0deg)" },
        "52%, 100%": { transform: "rotate(-120deg)" },
      },
      inner: {
        "0%, 26%": { transform: "rotate(0deg)" },
        "70%, 100%": { transform: "rotate(120deg)" },
      },
    },
  },
  // The ring holds still; the inner one steps a sixth, pauses pointing
  // down, and steps again
  tick: {
    cycleMs: 2400,
    easing: EASE,
    parts: {
      inner: {
        "0%, 10%": { transform: "rotate(0deg)" },
        "35%, 55%": { transform: "rotate(60deg)" },
        "80%, 100%": { transform: "rotate(120deg)" },
      },
    },
  },
  // The ring flips about its upright axis, the inner one follows
  coinFlip: {
    cycleMs: 2400,
    easing: EASE,
    parts: {
      ring: {
        "0%, 6%": { transform: "rotateY(0deg)" },
        "46%, 100%": { transform: "rotateY(180deg)" },
      },
      inner: {
        "0%, 28%": { transform: "rotateY(0deg)" },
        "68%, 100%": { transform: "rotateY(180deg)" },
      },
    },
  },
  // The inner one grows into the ring and fades into it, the ring answers
  // with a small pulse, and a new one grows from the centre
  emerge: {
    cycleMs: 2600,
    easing: EASE,
    parts: {
      ring: {
        "0%, 80%": { transform: "scale(1)" },
        "88%": { transform: "scale(1.045)" },
        "100%": { transform: "scale(1)" },
      },
      inner: {
        "0%": { transform: "scale(0)", opacity: 1 },
        "30%, 55%": { transform: "scale(1)", opacity: 1 },
        "88%, 100%": { transform: "scale(1.94)", opacity: 0 },
      },
    },
  },
  // The outline is traced, the ring fills, the inner one pops in; then all
  // fade and it starts over
  draw: {
    cycleMs: 3200,
    easing: EASE,
    parts: {
      trace: {
        "0%": { strokeDashoffset: 100, opacity: 1 },
        "35%, 80%": { strokeDashoffset: 0, opacity: 1 },
        "92%, 100%": { strokeDashoffset: 0, opacity: 0 },
      },
      ring: {
        "0%, 30%": { opacity: 0 },
        "45%, 80%": { opacity: 1 },
        "92%, 100%": { opacity: 0 },
      },
      inner: {
        "0%, 42%": { transform: "scale(0)", opacity: 1 },
        "54%": { transform: "scale(1.15)", opacity: 1 },
        "60%, 80%": { transform: "scale(1)", opacity: 1 },
        "92%, 100%": { transform: "scale(1)", opacity: 0 },
      },
    },
    partEasing: { ring: "ease" },
  },
  // The counter-turn, the coin flip and the emerge, one after the other.
  // Every keyframe names all three transforms, so that they blend.
  suite: {
    cycleMs: 7200,
    easing: EASE,
    parts: {
      ring: {
        "0%, 3%": { transform: "rotate(0deg) rotateY(0deg) scale(1)" },
        "17%, 35%": { transform: "rotate(-120deg) rotateY(0deg) scale(1)" },
        "49%, 90%": {
          transform: "rotate(-120deg) rotateY(180deg) scale(1)",
        },
        "95%": { transform: "rotate(-120deg) rotateY(180deg) scale(1.045)" },
        "100%": { transform: "rotate(-120deg) rotateY(180deg) scale(1)" },
      },
      inner: {
        "0%, 9%": {
          transform: "rotate(0deg) rotateY(0deg) scale(1)",
          opacity: 1,
        },
        "23%, 42%": {
          transform: "rotate(120deg) rotateY(0deg) scale(1)",
          opacity: 1,
        },
        "56%, 68%": {
          transform: "rotate(120deg) rotateY(180deg) scale(1)",
          opacity: 1,
        },
        "90%": {
          transform: "rotate(120deg) rotateY(180deg) scale(1.94)",
          opacity: 0,
        },
        "90.5%": {
          transform: "rotate(120deg) rotateY(180deg) scale(0)",
          opacity: 1,
        },
        "100%": {
          transform: "rotate(120deg) rotateY(180deg) scale(1)",
          opacity: 1,
        },
      },
    },
  },
  // The blue grows over the ring, a new ring emerges from its centre, a new
  // blue emerges in the ring's hole; then it rests on the logo. A layer
  // wholly covered is hidden, so that no hairline of it shows round the
  // edge of the one covering it.
  continuousEmerge: {
    cycleMs: 3000,
    easing: EASE,
    parts: {
      oldRing: {
        "0%, 42%": { opacity: 1 },
        "42.5%, 100%": { opacity: 0 },
      },
      cover: {
        "0%, 12%": { transform: "scale(1)", opacity: 1 },
        "42%, 70%": { transform: `scale(${INNER_TO_OUTER})`, opacity: 1 },
        "70.5%, 100%": { transform: `scale(${INNER_TO_OUTER})`, opacity: 0 },
      },
      newRing: {
        "0%, 34%": { transform: "scale(0)" },
        "70%, 100%": { transform: "scale(1)" },
      },
      newInner: {
        "0%, 60%": { transform: "scale(0)" },
        "92%, 100%": { transform: "scale(1)" },
      },
    },
    partEasing: { oldRing: "linear" },
  },
  // The same, each stage starting as the one before slows; never still
  continuousEmergeNoRest: {
    cycleMs: 2600,
    easing: SOFT_EASE,
    parts: {
      oldRing: {
        "0%, 38%": { opacity: 1 },
        "38.5%, 100%": { opacity: 0 },
      },
      cover: {
        "0%": { transform: "scale(1)", opacity: 1 },
        "38%, 66%": { transform: `scale(${INNER_TO_OUTER})`, opacity: 1 },
        "66.5%, 100%": { transform: `scale(${INNER_TO_OUTER})`, opacity: 0 },
      },
      newRing: {
        "0%, 22%": { transform: "scale(0)" },
        "66%, 100%": { transform: "scale(1)" },
      },
      newInner: {
        "0%, 52%": { transform: "scale(0)" },
        "100%": { transform: "scale(1)" },
      },
    },
    partEasing: { oldRing: "linear" },
  },
};

const keyframesName = (variant: LogoLoaderVariant, part: Part) =>
  `logo-${variant}-${part}`;

// The width and height (px) of the loader by what is loading: a whole view,
// a panel such as an entity's detail or a page of a form, or a section such
// as a dialog's content
export const LOGO_LOADER_SIZES = {
  view: 144,
  panel: 96,
  section: 64,
} as const;

export type LogoLoaderSize = keyof typeof LOGO_LOADER_SIZES;

export interface LogoLoaderProps {
  // How the mark moves
  variant?: LogoLoaderVariant;
  // The width and height of the square the moving mark takes: by what is
  // loading, or in px
  size?: LogoLoaderSize | number;
  // The surface the loader stands on. The ring's hole is painted in its
  // colour where a layer lies beneath, and the ring is toned towards it.
  surface?: "default" | "paper" | "raised";
  // Says what is loading, to those who cannot see the mark
  label?: string;
  // Room around the mark, e.g. above a line of text under it
  sx?: SxProps<Theme>;
}

// The logo's mark, moving while something loads. Still, in the logo's
// pose, for those who ask for less motion.
const LogoLoader: React.FC<LogoLoaderProps> = ({
  variant = "draw",
  size = "section",
  surface = "default",
  label,
  sx,
}) => {
  const motion = MOTIONS[variant];
  const parts = Object.keys(motion.parts) as Part[];
  const layered = "cover" in motion.parts;
  const px = typeof size === "number" ? size : LOGO_LOADER_SIZES[size];

  return (
    <Box
      component="svg"
      viewBox={VIEW_BOX}
      role="progressbar"
      aria-label={label ?? "Loading"}
      sx={[
        (theme) => {
        const ground = theme.palette.background[surface];
        return {
          width: px,
          height: px,
          display: "block",
          // In the middle of what holds it, whether that lays it out or only
          // centres its text
          mx: "auto",
          // The ring a little quieter than the text, opaque so that layers
          // can lie over one another
          "--logo-ring": `color-mix(in srgb, ${theme.palette.text.primary} 85%, ${ground})`,
          "--logo-ground": ground,
          ...Object.fromEntries(
            parts.map((part) => [
              `@keyframes ${keyframesName(variant, part)}`,
              motion.parts[part],
            ]),
          ),
          ...Object.fromEntries(
            parts.map((part) => [
              `& [data-part="${part}"]`,
              {
                animation: `${keyframesName(variant, part)} ${motion.cycleMs}ms ${
                  motion.partEasing?.[part] ?? motion.easing
                } infinite`,
              },
            ]),
          ),
          "& [data-part]": {
            transformBox: "view-box",
            transformOrigin: CENTRE,
          },
          "@media (prefers-reduced-motion: reduce)": {
            "& [data-part]": { animation: "none !important" },
          },
        };
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {layered ? (
        <>
          <g data-part="oldRing">
            <path d={OUTER} style={{ fill: "var(--logo-ring)" }} />
            <path d={HOLE} style={{ fill: "var(--logo-ground)" }} />
          </g>
          <path data-part="cover" d={INNER} fill={LOGO_BLUE} />
          <g data-part="newRing">
            <path d={OUTER} style={{ fill: "var(--logo-ring)" }} />
            <path d={HOLE} style={{ fill: "var(--logo-ground)" }} />
          </g>
          <path data-part="newInner" d={INNER} fill={LOGO_BLUE} />
        </>
      ) : (
        <>
          <path
            data-part="ring"
            d={`${OUTER} ${HOLE}`}
            fillRule="evenodd"
            style={{ fill: "var(--logo-ring)" }}
          />
          {variant === "draw" && (
            <path
              data-part="trace"
              d={`${OUTER} ${HOLE}`}
              pathLength={100}
              fill="none"
              style={{ stroke: "var(--logo-ring)" }}
              strokeWidth={12}
              strokeLinejoin="round"
              strokeDasharray="100 100"
            />
          )}
          <path data-part="inner" d={INNER} fill={LOGO_BLUE} />
        </>
      )}
    </Box>
  );
};

export default LogoLoader;
